import React, { useRef, useState } from 'react'
import { useFrame } from '@react-three/fiber'
import { Trail, Text, Billboard, Ring } from '@react-three/drei'
import * as THREE from 'three'
import useAppStore from '../stores/appStore'

export function PoemNode({
  position,
  poemIndex,
  chapterIndex,
  isActive = false,
  onClick,
  enableTrail = true,
  color = '#FFD700' // Gold
}) {
  const meshRef = useRef()
  const ringRef = useRef()
  const outerRingRef = useRef()
  const [hovered, setHovered] = useState(false)
  const { currentPoem, setCurrentPoem } = useAppStore()

  // Check if this is the currently selected poem
  const isSelected = currentPoem === poemIndex

  // Display number (1-9 instead of 0-8)
  const displayNumber = poemIndex + 1

  // Smooth position interpolation
  const targetPos = useRef(new THREE.Vector3(...position))
  const currentPos = useRef(new THREE.Vector3(...position))

  // Ring animation values
  const ringOpacity = useRef(0)

  useFrame((state, delta) => {
    if (!meshRef.current) return

    // Update target position
    targetPos.current.set(position[0], position[1], position[2])

    // Smooth interpolation
    currentPos.current.lerp(targetPos.current, delta * 5)
    meshRef.current.position.copy(currentPos.current)

    // Idle float animation
    const idleFloat = Math.sin(state.clock.elapsedTime * 2 + poemIndex * 0.5) * 0.02
    meshRef.current.position.y += idleFloat

    // Hover/active/selected scale (clamped to prevent extreme sizes)
    const targetScale = Math.min(2.0, (hovered ? 1.5 : 1) * (isActive ? 1.3 : 1) * (isSelected ? 1.4 : 1))
    meshRef.current.scale.lerp(
      new THREE.Vector3(targetScale, targetScale, targetScale),
      delta * 10
    )

    // Gentle rotation
    meshRef.current.rotation.y += delta * 0.5

    // Animate ring for selected/hovered state
    if (ringRef.current) {
      const targetRingOpacity = isSelected ? 1 : (hovered ? 0.7 : 0)
      ringOpacity.current = THREE.MathUtils.lerp(ringOpacity.current, targetRingOpacity, delta * 8)
      ringRef.current.material.opacity = ringOpacity.current

      // Pulsing ring scale when selected
      if (isSelected) {
        const pulse = 1 + Math.sin(state.clock.elapsedTime * 3) * 0.1
        ringRef.current.scale.setScalar(pulse)
      }

      // Rotate ring
      ringRef.current.rotation.z += delta * (isSelected ? 1.5 : 0.5)
    }

    // Outer ring animation (selection indicator)
    if (outerRingRef.current) {
      const targetOuterOpacity = isSelected ? 0.8 : 0
      outerRingRef.current.material.opacity = THREE.MathUtils.lerp(
        outerRingRef.current.material.opacity,
        targetOuterOpacity,
        delta * 6
      )
      // Counter-rotate outer ring
      outerRingRef.current.rotation.z -= delta * 0.8
    }
  })

  const handleClick = (e) => {
    e.stopPropagation()
    // Update app store to show this poem
    setCurrentPoem(poemIndex)
    // Call any additional onClick handler if provided
    onClick?.(poemIndex, chapterIndex)
  }

  const NodeMesh = () => (
    <group ref={meshRef}>
      {/* Main sphere */}
      <mesh
        onClick={handleClick}
        onPointerOver={(e) => {
          e.stopPropagation()
          setHovered(true)
        }}
        onPointerOut={(e) => {
          e.stopPropagation()
          setHovered(false)
        }}
      >
        <sphereGeometry args={[0.1, 32, 32]} />
        <meshStandardMaterial
          color={color}
          emissive={color}
          emissiveIntensity={isSelected ? 3.5 : (isActive ? 2 : (hovered ? 2 : 0.8))}
          metalness={0.8}
          roughness={0.2}
        />
      </mesh>

      {/* Inner highlight ring - appears on hover/select */}
      <Ring
        ref={ringRef}
        args={[0.14, 0.18, 32]}
        rotation={[Math.PI / 2, 0, 0]}
      >
        <meshBasicMaterial
          color={color}
          transparent
          opacity={0}
          side={THREE.DoubleSide}
        />
      </Ring>

      {/* Outer selection ring - appears when selected */}
      <Ring
        ref={outerRingRef}
        args={[0.22, 0.25, 6]}
        rotation={[Math.PI / 2, 0, 0]}
      >
        <meshBasicMaterial
          color="#ffffff"
          transparent
          opacity={0}
          side={THREE.DoubleSide}
        />
      </Ring>

      {/* Number label - always visible, billboarded to face camera */}
      <Billboard
        follow={true}
        lockX={false}
        lockY={false}
        lockZ={false}
      >
        <Text
          position={[0, 0.22, 0]}
          fontSize={0.1}
          color={isSelected ? '#ffffff' : (hovered ? '#ffffff' : color)}
          anchorX="center"
          anchorY="middle"
          outlineWidth={0.015}
          outlineColor={isSelected ? color : '#000000'}
          fontWeight="bold"
        >
          {displayNumber}
        </Text>
      </Billboard>

      {/* Glow effect */}
      <pointLight
        color={color}
        intensity={isSelected ? 5 : (isActive ? 3 : (hovered ? 2.5 : 1))}
        distance={isSelected ? 3 : 2}
        decay={2}
      />

      {/* Additional bloom-friendly glow sphere for selected state */}
      {isSelected && (
        <mesh scale={1.8}>
          <sphereGeometry args={[0.1, 16, 16]} />
          <meshBasicMaterial
            color={color}
            transparent
            opacity={0.15}
          />
        </mesh>
      )}
    </group>
  )

  if (enableTrail) {
    return (
      <Trail
        width={0.5}
        length={6}
        color={color}
        attenuation={(t) => t * t}
        decay={0.95}
      >
        <NodeMesh />
      </Trail>
    )
  }

  return <NodeMesh />
}

export default PoemNode
